import { GeneralUser, User } from '@/types';
import axios, { AxiosError } from 'axios';
import api, { evictSession } from 'src/client/axiosInstance';

export type UploadDocumentResponse = {
  file: string;
  publicName: string;
  size: number;
};

const handleError = (error: unknown, message: string): never => {
  if (axios.isAxiosError(error)) {
    throw new Error(
      `${message}: ${error.response?.data?.message || error.message}`,
    );
  } else {
    throw new Error(`${message}: Unknown error occurred`);
  }
};

export const getProfile = async () => {
  try {
    const response = await api.get('users/profile');
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const updateProfile = async ({
  data,
  avatar,
}: {
  data: Partial<GeneralUser>;
  avatar?: File;
}) => {
  try {
    const formData = new FormData();
    const sanitizedKeys = ['established', 'startedFranchising'];
    Object.entries(data).forEach(([key, value]) => {
      if (
        sanitizedKeys.includes(key) ||
        (value !== undefined && value !== null)
      ) {
        if (Array.isArray(value)) {
          if (value.length === 0) {
            if (key === 'documents' || key === 'fddFiles') {
              formData.append(key, '');
            } else {
              formData.append(key, '');
            }
          } else {
            if (key === 'documents') {
              value.forEach((doc) => {
                formData.append('documents[]', JSON.stringify(doc));
              });
            } else if (key === 'fddFiles') {
              value.forEach((fddFile) => {
                formData.append('fddFiles[]', JSON.stringify(fddFile));
              });
            } else if (key === 'videos') {
              value.forEach((video) => {
                formData.append('videos[]', JSON.stringify(video));
              });
            } else {
              value.forEach((item) => {
                formData.append(`${key}[]`, item.toString());
              });
            }
          }
        } else {
          formData.append(
            key,
            typeof value === 'boolean'
              ? value.toString()
              : value !== undefined && value !== null
              ? value.toString()
              : '',
          );
        }
      }
    });
    if (avatar) {
      formData.append('profilePhoto', avatar);
    }

    const response = await api.patch('users', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error: any) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw error;
  }
};

export const updateAccountStatus = async (data: { status: boolean }) => {
  try {
    const response = await api.patch('users/account-visibility', data);
    return response.data;
  } catch (error: any) {
    const message = error.response?.data?.message || 'Failed to update status';
    throw new Error(message);
  }
};

export const deleteAccount = async () => {
  try {
    const response = await api.delete('users');
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
  }
};

export const changeBillingAddress = async (data: { email?: string }) => {
  if (!data.email) {
    throw new Error('Email is required');
  }
  try {
    const response = await api.patch('users/change-stripe-email', data);
    return response.data;
  } catch (e: any) {
    if (e instanceof AxiosError) {
      throw new Error(
        e.response?.data?.message || 'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const updateBillingContacts = async (billingContacts: string[]) => {
  if (!billingContacts?.length) {
    throw new Error('At least one billing contact is required');
  }
  try {
    const response = await api.patch('users/billing-contacts', {
      billingContacts,
    });
    return response.data;
  } catch (e: any) {
    if (e instanceof AxiosError) {
      throw new Error(
        e.response?.data?.message || 'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const changePassword = async (data: {
  password: string;
  confirmPassword: string;
  oldPassword: string;
}) => {
  try {
    const response = await api.patch('users/password', data);
    return response.data;
  } catch (e: any) {
    if (e instanceof AxiosError) {
      // If it's a 401 error after password change, it means tokens were invalidated
      // server-side — a definitively-invalid session. TCX-795: route through the
      // single shared eviction path in axiosInstance instead of duplicating the
      // clear + redirect logic here.
      if (e.response?.status === 401) {
        evictSession();
        throw new Error('Password changed successfully. Please log in again.');
      }
      throw new Error(
        e.response?.data?.message || 'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const requestSupport = async (data: {
  email: string;
  details?: string;
}): Promise<boolean> => {
  try {
    const response = await api.post('users/support-request', data);
    return response.data;
  } catch (e: any) {
    if (e instanceof AxiosError) {
      throw new Error(
        e.response?.data?.message || 'Something went wrong. Please try again.',
      );
    } else {
      throw new Error('Unknown error occurred');
    }
  }
};

export const requestEmailChange = async (data: {
  email: string;
}): Promise<{ message: string }> => {
  try {
    const response = await api.post('users/request-email-change', data);
    return response.data;
  } catch (e: any) {
    if (e instanceof AxiosError) {
      throw new Error(
        e.response?.data?.message || 'Failed to send verification code.',
      );
    }
    throw new Error('Unknown error occurred');
  }
};

export const confirmEmailChange = async (data: {
  code: string;
}): Promise<{ message: string; billingContactsSynced?: boolean }> => {
  try {
    const response = await api.post('users/confirm-email-change', data);
    return response.data;
  } catch (e: any) {
    if (e instanceof AxiosError) {
      throw new Error(
        e.response?.data?.message || 'Failed to verify the code.',
      );
    }
    throw new Error('Unknown error occurred');
  }
};

export const sendCompanySupportRequest = async (data: {
  email: string;
  company: string;
  firstName: string;
  lastName: string;
}) => {
  try {
    const response = await api.post('users/company-support-request', data);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
  }
};

export const uploadImages = async (data: File, type: string) => {
  try {
    const formData = new FormData();
    formData.append('image', data);
    formData.append('type', type);

    const response = await api.post('users/upload-images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
  }
};

export const validatePromoCode = async (data: { promoCode: string }) => {
  try {
    const response = await api.post('users/validate-promo-code', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Error validating promo code');
  }
};

export const subscribe = async (data?: {
  promoCode?: string;
  priceId?: string;
  paymentMethodId?: string;
}) => {
  try {
    const response = await api.post('users/subscribe', {
      promoCode: data?.promoCode,
      priceId: data?.priceId,
      paymentMethodId: data?.paymentMethodId,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error subscribing');
  }
};

export const unsubscribe = async () => {
  try {
    const response = await api.post('users/unsubscribe');
    return response.data;
  } catch (error) {
    handleError(error, 'Error unsubscribing');
  }
};

export const reactivateSubscription = async () => {
  try {
    const response = await api.post('users/reactivate-subscription');
    return response.data;
  } catch (error) {
    handleError(error, 'Error reactivating subscription');
  }
};

// TCX-455: franchisor cancellation request — emails support, no Stripe action.
export const requestSubscriptionCancellation = async (reason: string) => {
  try {
    const response = await api.post('users/request-cancellation', { reason });
    return response.data;
  } catch (error) {
    return handleError(error, 'Error submitting cancellation request');
  }
};

// TCX-839: franchisor Connect→premium upgrade request — emails the admin
// team, no Stripe action (the admin performs the plan change by hand).
export const requestSubscriptionUpgrade = async (priceId: string) => {
  try {
    const response = await api.post('users/request-upgrade', { priceId });
    return response.data;
  } catch (error) {
    return handleError(error, 'Error submitting upgrade request');
  }
};

export const createBillingPortalSession = async (): Promise<{
  url: string;
}> => {
  try {
    const response = await api.post('users/billing-portal');
    return response.data;
  } catch (error) {
    throw handleError(error, 'Error creating billing portal session');
  }
};

export const getFilters = async () => {
  try {
    const response = await api.get('users/filters');
    return response.data;
  } catch (error) {
    handleError(error, 'Error getting filters');
  }
};

export const getCalendars = async () => {
  try {
    const response = await api.get('users/calendars');
    return response.data;
  } catch (error) {
    handleError(error, 'Error getting calendars');
  }
};

export const updateCalendar = async ({
  data,
}: {
  data: {
    calendar_id: string;
  };
}) => {
  try {
    const response = await api.patch('users/calendars', data);
    return response.data;
  } catch (error) {
    handleError(error, 'Error updating calendar');
  }
};

export const updateTimeZone = async (timeZone: string) => {
  try {
    const response = await api.patch('users/timezone', { timezone: timeZone });
    return response.data;
  } catch (error) {
    handleError(error, 'Error updating timezone');
  }
};
